import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, Trophy, X } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar from '../../layout/ListToolbar';
import KDoreseniRadek, { KDoreseniSeznam } from '../../layout/KDoreseni';
import ZpetTlacitko from '../../ui/ZpetTlacitko';
import ModalShell from '../../ui/ModalShell';
import usePotvrzeni from '../../ui/usePotvrzeni';
import SidePanel from '../../SidePanel';
import Dropdown from '../../receptura/Dropdown';
import { SECTION_COLOR } from '../lide';

/**
 * Lidé → Kariéry (revize Nastavení, etapa 3, 2026-10-12).
 *
 * Jedna kariéra = jedna karta. Dřív se skládala ze čtyř míst starého
 * Nastavení: Zaměstnanci › Kariéry (sazby, certifikáty), Vzdělávání ›
 * Kariérní požadavky, KPI katalog a Konfigurace › Kariéry (jediná uměla
 * řízenou dokumentaci).
 *
 * Norma §1: seznam kariér · detail ve sloupci (úrovně se sazbou, počtem
 * lidí a požadavky; certifikáty; řízená dokumentace; KPI) · úprava úrovně
 * v inspektoru (§1f) · třetí sloupec `panelVzdy` — lidé na úrovních
 * a „K dořešení" (úroveň bez sazby, kariéra bez lidí).
 */
const CERVENA = '#EF4444';
const DRUHY_DOKUMENTACE = [{ kod: 'smernice', nazev: 'Směrnice' }, { kod: 'recepty', nazev: 'Recepty' }];
const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';
async function api(url, metoda = 'GET', data) {
    const r = await fetch(url, {
        method: metoda, credentials: 'same-origin',
        headers: { Accept: 'application/json', ...(data !== undefined ? { 'Content-Type': 'application/json' } : {}), 'X-CSRF-TOKEN': csrf() },
        body: data !== undefined ? JSON.stringify(data) : undefined,
    });
    if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.errors ? Object.values(d.errors).flat()[0] : d?.error || d?.message || `Chyba ${r.status}`);
    }
    return r.status === 204 ? null : r.json().catch(() => null);
}
const kc = (v) => (v == null || v === '' ? null : `${Math.round(Number(v)).toLocaleString('cs-CZ')} Kč/h`);

/** Kolik lidí je na které úrovni kariéry — z `urovne` zaměstnanců. */
function lideNaUrovnich(zamestnanci, karieraId) {
    const m = {};
    for (const z of zamestnanci) {
        for (const u of z.urovne || []) {
            const kid = u.kariera_id ?? u.kariera?.id;
            const uid = u.uroven_id ?? u.uroven?.id;
            if (kid === karieraId && uid) m[uid] = (m[uid] || 0) + 1;
        }
    }
    return m;
}

export function kDoreseniKarier(kariery, zamestnanci) {
    const out = [];
    for (const k of kariery) {
        if (k.aktivni === false) continue;
        const bez = (k.urovne || []).filter((u) => u.sazba_kc_h == null || Number(u.sazba_kc_h) === 0);
        if (bez.length) out.push({ id: `sazba-${k.id}`, karieraId: k.id, problem: true, titulek: `${k.nazev} · bez sazby`, popis: bez.map((u) => u.nazev).join(', ') });
        const lidi = Object.values(lideNaUrovnich(zamestnanci, k.id)).reduce((a, b) => a + b, 0);
        if (!lidi) out.push({ id: `prazdna-${k.id}`, karieraId: k.id, problem: false, titulek: `${k.nazev} · nikdo`, popis: 'V kariéře není žádný člověk.' });
    }
    return out.sort((a, b) => Number(b.problem) - Number(a.problem));
}

export default function Kariery({ zamestnanci = [], upravuje, isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole }) {
    const [kariery, setKariery] = useState([]);
    const [pozadavky, setPozadavky] = useState({});   // uroven_id → [požadavek]
    const [dovednosti, setDovednosti] = useState([]);
    const [certifikace, setCertifikace] = useState([]);
    const [kpi, setKpi] = useState([]);
    const [nacteno, setNacteno] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [vybranaId, setVybranaId] = useState(null);
    const [uroven, setUroven] = useState(null);       // inspektor úrovně
    const [uprava, setUprava] = useState(null);       // 'nova' | kariéra
    const [hledani, setHledani] = useState('');
    const [potvrdit, potvrzeni] = usePotvrzeni(isDark);

    const obnov = useCallback(() => Promise.all([
        api('/api/ciselniky/kariery').then((d) => setKariery(Array.isArray(d) ? d : [])),
        api('/api/konfigurace/kariera-pozadavky').then((d) => {
            const m = {};
            for (const k of d || []) for (const u of k.urovne || []) m[u.id] = u.pozadavky || [];
            setPozadavky(m);
        }),
        api('/api/ciselniky/dovednosti').then((d) => setDovednosti(Array.isArray(d) ? d : [])).catch(() => {}),
        api('/api/certifikace').then((d) => setCertifikace(Array.isArray(d) ? d : [])).catch(() => {}),
        api('/api/bonus/kpi').then((d) => setKpi(Array.isArray(d) ? d : [])).catch(() => {}),
    ]).catch((e) => setChyba(e.message)).finally(() => setNacteno(true)), []);
    useEffect(() => { obnov(); }, [obnov]);

    // Horní hrana obsahu pro inspektor (§1f)
    const obsahRef = useRef(null);
    const [top, setTop] = useState(0);
    useEffect(() => {
        const zmer = () => { const el = obsahRef.current; if (el) setTop(Math.max(0, Math.round(el.getBoundingClientRect().top))); };
        zmer(); window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [uroven]);

    const kDoreseni = useMemo(() => kDoreseniKarier(kariery, zamestnanci), [kariery, zamestnanci]);
    const vybrana = kariery.find((k) => k.id === vybranaId) || null;
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const ml = mutedColor;
    const q = hledani.trim().toLowerCase();
    const seznamKarier = kariery.filter((k) => !q || k.nazev.toLowerCase().includes(q));

    const ulozKarieru = async (k, zmena) => {
        try { await api(`/api/konfigurace/kariery/${k.id}${zmena.cesta}`, 'PUT', zmena.data); await obnov(); }
        catch (e) { setChyba(e.message); }
    };
    const prepniCert = (k, kod) => {
        const nyni = k.pozadovane_certifikace || [];
        ulozKarieru(k, { cesta: '/certifikace', data: { pozadovane_certifikace: nyni.includes(kod) ? nyni.filter((c) => c !== kod) : [...nyni, kod] } });
    };
    const prepniDok = (k, kod) => {
        const nyni = k.druhy_dokumentace || [];
        ulozKarieru(k, { cesta: '/dokumentace', data: { druhy_dokumentace: nyni.includes(kod) ? nyni.filter((c) => c !== kod) : [...nyni, kod] } });
    };
    const smaz = async (k) => {
        if (!(await potvrdit(`Smazat kariéru „${k.nazev}"?`, { text: 'Smažou se i její úrovně, sazby a požadavky.', potvrdit: 'Smazat' }))) return;
        try { await api(`/api/konfigurace/kariery/${k.id}`, 'DELETE'); setVybranaId(null); await obnov(); }
        catch (e) { setChyba(e.message); }
    };

    const radekStyl = (sel) => ({
        gap: 12, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: 56, cursor: 'pointer', border: 'none',
        borderBottom: `1px solid ${cara}`, background: sel ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)') : 'transparent',
        boxShadow: sel ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
    });

    const seznam = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={ml} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat kariéru…', placeholderKratky: 'Hledat' }}
                akce={upravuje && !isMobile ? [{ id: 'nova', label: 'Nová kariéra', Icon: Plus, primarni: true, onClick: () => setUprava('nova') }] : []}
                pod={isMobile && kDoreseni.length > 0 ? <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} styl={{ marginTop: 8 }}
                    renderAkce={(k) => <button type="button" onClick={() => setVybranaId(k.karieraId)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} /> : null} />
            {siroky && (
                <div className="flex items-center" style={{ gap: 12, padding: '8px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: ml, borderBottom: `1px solid ${cara}` }}>
                    <span style={{ width: 24 }} />
                    <span className="flex-1">Kariéra</span>
                    <span style={{ width: 70, textAlign: 'right' }}>Lidí</span>
                    <span style={{ width: 150, textAlign: 'right' }}>Sazby</span>
                    <span style={{ width: 160 }}>Certifikáty</span>
                </div>
            )}
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {!nacteno && <p className="text-sm" style={{ color: ml, padding: 16 }}>Načítám…</p>}
                {seznamKarier.map((k) => {
                    const lidi = Object.values(lideNaUrovnich(zamestnanci, k.id)).reduce((a, b) => a + b, 0);
                    const sazby = (k.urovne || []).map((u) => Number(u.sazba_kc_h)).filter((v) => v > 0);
                    const problem = kDoreseni.some((x) => x.karieraId === k.id && x.problem);
                    return (
                        <button key={k.id} type="button" onClick={() => setVybranaId(k.id)} className="w-full text-left flex items-center" style={radekStyl(k.id === vybranaId)}>
                            <span style={{ width: 24, textAlign: 'center', fontSize: 18 }}>{k.ikona || '•'}</span>
                            <div className="flex-1 min-w-0">
                                <p className="truncate font-semibold" style={{ margin: 0, color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{k.nazev}</p>
                                {!siroky && <p className="truncate" style={{ margin: '2px 0 0', fontSize: 11.5, color: ml }}>{(k.urovne || []).length} úrovní · {lidi} lidí</p>}
                            </div>
                            {siroky && <span style={{ width: 70, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{lidi}</span>}
                            {siroky && <span style={{ width: 150, textAlign: 'right', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: problem ? CERVENA : 'var(--text-primary)', fontWeight: problem ? 700 : 500 }}>
                                {sazby.length ? `${Math.min(...sazby)}–${Math.max(...sazby)} Kč/h` : '—'}</span>}
                            {siroky && <span className="truncate" style={{ width: 160, fontSize: 12.5, color: ml }}>{(k.pozadovane_certifikace || []).join(', ') || '—'}</span>}
                            {!siroky && problem && <span aria-label="K dořešení" style={{ width: 8, height: 8, borderRadius: 4, background: CERVENA, flexShrink: 0 }} />}
                        </button>
                    );
                })}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detail = () => {
        const k = vybrana;
        if (!k) return null;
        const lidi = lideNaUrovnich(zamestnanci, k.id);
        const kpiKariery = kpi.filter((x) => x.kariera_id === k.id);
        const nadpis = (t, mt = 18) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: ml, margin: `${mt}px 0 8px` }}>{t}</p>;
        const chip = (aktivni, label, onClick) => (
            <button key={label} type="button" onClick={upravuje ? onClick : undefined} disabled={!upravuje}
                style={{ minHeight: isMobile ? 40 : 32, padding: '0 12px', borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: upravuje ? 'pointer' : 'default',
                    background: 'transparent', color: aktivni ? 'var(--text-primary)' : ml,
                    border: `1px solid ${aktivni ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
                    boxShadow: aktivni ? `inset 0 0 0 1px ${SECTION_COLOR}` : 'none' }}>
                {aktivni ? '✓ ' : ''}{label}
            </button>
        );
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '6px 14px 10px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && <ZpetTlacitko onClick={() => setVybranaId(null)} popis="Zpět na kariéry" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <h2 className="font-bold" style={{ fontSize: 18, margin: 0, color: 'var(--text-primary)' }}>{k.ikona} {k.nazev}</h2>
                            <p className="text-xs" style={{ color: ml, margin: '4px 0 0' }}>{[`${(k.urovne || []).length} úrovní`, k.kod, k.popis].filter(Boolean).join(' · ')}</p>
                        </div>
                        {upravuje && !isMobile && (
                            <button type="button" onClick={() => setUprava(k)} className="inline-flex items-center gap-1.5 rounded-lg"
                                style={{ height: 34, padding: '0 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                                <Pencil size={14} /> Upravit
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '8px 16px' : '6px 18px 18px' }}>
                    {chyba && <p className="text-sm" style={{ color: CERVENA }}>{chyba}</p>}
                    {nadpis('Úrovně a sazby', 12)}
                    {(k.urovne || []).slice().sort((a, b) => a.poradi - b.poradi).map((u) => {
                        const poz = pozadavky[u.id] || [];
                        const bezSazby = u.sazba_kc_h == null || Number(u.sazba_kc_h) === 0;
                        return (
                            <button key={u.id} type="button" onClick={() => setUroven({ ...u, kariera: k })} className="w-full text-left flex items-center"
                                style={{ gap: 12, minHeight: isMobile ? 56 : 44, padding: '6px 4px', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer',
                                    background: uroven?.id === u.id ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)') : 'transparent' }}>
                                <span className="flex-1 min-w-0">
                                    <span className="block font-semibold" style={{ fontSize: 13.5, color: 'var(--text-primary)' }}>{u.nazev}</span>
                                    <span className="block truncate" style={{ fontSize: 11.5, color: ml }}>
                                        {poz.length ? poz.map((p) => `${p.dovednost?.nazev || '?'} ≥ ${p.min_uroven}`).join(' · ') : 'bez požadavků na dovednosti'}
                                    </span>
                                </span>
                                <span style={{ width: 60, textAlign: 'right', fontSize: 12, color: ml }}>{lidi[u.id] || 0} lidí</span>
                                <span style={{ width: 100, textAlign: 'right', fontSize: 13, fontWeight: bezSazby ? 700 : 600, fontVariantNumeric: 'tabular-nums', color: bezSazby ? CERVENA : 'var(--text-primary)' }}>
                                    {kc(u.sazba_kc_h) || 'bez sazby'}
                                </span>
                            </button>
                        );
                    })}
                    <p className="text-xs" style={{ color: ml, margin: '6px 0 0' }}>Klikni na úroveň — sazba a požadované dovednosti se upravují v panelu vpravo.</p>

                    {nadpis('Požadované certifikáty')}
                    <p className="text-xs" style={{ color: ml, margin: '0 0 8px' }}>Bez platného certifikátu je člověk v kariéře nejvýš Novice.</p>
                    <div className="flex flex-wrap" style={{ gap: 8 }}>
                        {certifikace.map((c) => chip((k.pozadovane_certifikace || []).includes(c.kod), `${c.odznak || ''} ${c.nazev}`.trim(), () => prepniCert(k, c.kod)))}
                        {!certifikace.length && <span className="text-sm" style={{ color: ml }}>Žádné typy certifikátů.</span>}
                    </div>

                    {nadpis('Řízená dokumentace v Cockpitu')}
                    <div className="flex flex-wrap" style={{ gap: 8 }}>
                        {DRUHY_DOKUMENTACE.map((d) => chip((k.druhy_dokumentace || []).includes(d.kod), d.nazev, () => prepniDok(k, d.kod)))}
                    </div>

                    {nadpis('KPI pro bonus')}
                    {kpiKariery.length ? kpiKariery.map((x) => (
                        <div key={x.id} className="flex items-center" style={{ gap: 10, minHeight: 36, borderBottom: `1px solid ${cara}`, fontSize: 13 }}>
                            <span className="flex-1" style={{ color: 'var(--text-primary)' }}>{x.nazev}</span>
                            <span style={{ color: ml, fontSize: 12 }}>{x.typ === 'auto' ? 'automaticky' : 'ručně'}{x.threshold_pct != null ? ` · práh ${x.threshold_pct} %` : ''}</span>
                            <b style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{x.max_bonus_kc != null ? `${Math.round(x.max_bonus_kc)} Kč` : '—'}</b>
                        </div>
                    )) : <p className="text-sm" style={{ color: ml, margin: 0 }}>Kariéra nemá vlastní KPI.</p>}
                    <p className="text-xs" style={{ color: ml, margin: '6px 0 0' }}>Katalog KPI se upravuje v ⚙ Nastavení Lidí.</p>

                    {upravuje && !isMobile && (
                        <div style={{ marginTop: 24, paddingTop: 14, borderTop: `1px solid ${cara}` }}>
                            <button type="button" onClick={() => smaz(k)} className="inline-flex items-center gap-1.5 rounded-lg"
                                style={{ height: 34, padding: '0 12px', fontSize: 13, cursor: 'pointer', background: 'transparent', color: CERVENA, border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                                <Trash2 size={14} /> Smazat kariéru
                            </button>
                        </div>
                    )}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const panel = () => {
        const k = vybrana;
        const lidi = k ? lideNaUrovnich(zamestnanci, k.id) : null;
        const radek = (l, h, barva) => (
            <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}>
                <span style={{ color: ml }}>{l}</span><b style={{ color: barva || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{h}</b>
            </div>
        );
        const nadpis = (t, mt = 0) => <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: ml, margin: `${mt}px 0 6px` }}>{t}</p>;
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                {k ? (
                    <>
                        {nadpis(`Lidé · ${k.nazev}`)}
                        {(k.urovne || []).slice().sort((a, b) => a.poradi - b.poradi).map((u) => <div key={u.id}>{radek(u.nazev, lidi[u.id] || 0)}</div>)}
                    </>
                ) : (
                    <>
                        {nadpis('Kariéry')}
                        {radek('Kariér', kariery.length)}
                        {radek('Úrovní', kariery.reduce((a, k2) => a + (k2.urovne || []).length, 0))}
                        {radek('Lidí v kariérách', zamestnanci.filter((z) => (z.urovne || []).length).length)}
                    </>
                )}
                {nadpis('K dořešení', 16)}
                <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} prazdne={nacteno ? 'U kariér je všechno v pořádku.' : 'Načítám…'}
                    renderAkce={(x) => <button type="button" onClick={() => setVybranaId(x.karieraId)} style={{ fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>Otevřít</button>} />
            </div>
        );
    };

    return (
        <div ref={obsahRef} className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybrana} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznam} detail={detail} panel={panel} />
            {uroven && (
                <SidePanel open onClose={() => setUroven(null)} title={`${uroven.kariera.nazev} · ${uroven.nazev}`} modal={false} width={420}
                    isDark={isDark} isMobile={isMobile} accent={SECTION_COLOR} topOffset={top}>
                    <InspektorUrovne key={uroven.id} uroven={uroven} pozadavky={pozadavky[uroven.id] || []} dovednosti={dovednosti} upravuje={upravuje}
                        isDark={isDark} inputStyle={inputStyle} mutedColor={ml} onZmena={obnov} />
                </SidePanel>
            )}
            {uprava && <UpravaKariery kariera={uprava === 'nova' ? null : uprava} isDark={isDark} isMobile={isMobile} inputStyle={inputStyle}
                onClose={() => setUprava(null)} onUlozeno={async (k) => { setUprava(null); await obnov(); if (k?.id) setVybranaId(k.id); }} />}
            {potvrzeni}
        </div>
    );
}

function InspektorUrovne({ uroven, pozadavky, dovednosti, upravuje, isDark, inputStyle, mutedColor, onZmena }) {
    const [sazba, setSazba] = useState(uroven.sazba_kc_h ?? '');
    const [nova, setNova] = useState({ dovednost_id: '', min_uroven: '3' });
    const [chyba, setChyba] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const urovne = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `úroveň ${n}` }));
    const zbyvajici = dovednosti.filter((d) => !pozadavky.some((p) => p.dovednost_id === d.id));

    const zkus = async (fn) => { setChyba(null); setUkladam(true); try { await fn(); await onZmena(); } catch (e) { setChyba(e.message); } setUkladam(false); };
    const ulozSazbu = () => zkus(() => api(`/api/konfigurace/sazby/${uroven.id}`, 'PUT', { sazba_kc_h: sazba === '' ? null : Number(sazba) }));
    const pridej = () => nova.dovednost_id && zkus(async () => {
        await api('/api/konfigurace/kariera-pozadavky', 'POST', { kariera_uroven_id: uroven.id, dovednost_id: Number(nova.dovednost_id), min_uroven: Number(nova.min_uroven) });
        setNova({ dovednost_id: '', min_uroven: '3' });
    });
    const zmenMin = (p, v) => zkus(() => api(`/api/konfigurace/kariera-pozadavky/${p.id}`, 'PUT', { min_uroven: Number(v) }));
    const odeber = (p) => zkus(() => api(`/api/konfigurace/kariera-pozadavky/${p.id}`, 'DELETE'));

    return (
        <div style={{ padding: 16 }}>
            {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '0 0 10px' }}>{chyba}</p>}
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Hodinová sazba (Kč/h)</label>
            <div className="flex" style={{ gap: 8 }}>
                <input type="number" min="0" step="1" value={sazba} onChange={(e) => setSazba(e.target.value)} disabled={!upravuje} style={{ ...inputStyle, flex: 1, height: 38 }} />
                {upravuje && (
                    <button type="button" onClick={ulozSazbu} disabled={ukladam || String(sazba) === String(uroven.sazba_kc_h ?? '')}
                        style={{ height: 38, padding: '0 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer', background: SECTION_COLOR, color: '#fff', opacity: String(sazba) === String(uroven.sazba_kc_h ?? '') ? 0.4 : 1 }}>
                        Uložit
                    </button>
                )}
            </div>
            <p className="text-xs" style={{ color: mutedColor, margin: '6px 0 0' }}>Po uložení se přepočítají náklady všech lidí na této úrovni.</p>

            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: mutedColor, margin: '20px 0 6px' }}>Požadované dovednosti</p>
            {pozadavky.length === 0 && <p className="text-sm" style={{ color: mutedColor, margin: 0 }}>Bez požadavků.</p>}
            {pozadavky.map((p) => (
                <div key={p.id} className="flex items-center" style={{ gap: 8, minHeight: 44, borderBottom: `1px solid ${cara}` }}>
                    <span className="flex-1 min-w-0 truncate" style={{ fontSize: 13, color: 'var(--text-primary)' }}>{p.dovednost?.nazev}</span>
                    {upravuje ? (
                        <div style={{ width: 130 }}><Dropdown value={String(p.min_uroven)} isDark={isDark} accent={SECTION_COLOR} height={34} onChange={(v) => zmenMin(p, v)} options={urovne} /></div>
                    ) : <span style={{ fontSize: 12.5, color: mutedColor }}>≥ {p.min_uroven}</span>}
                    {upravuje && (
                        <button type="button" onClick={() => odeber(p)} aria-label="Odebrat požadavek"
                            style={{ width: 34, height: 34, border: 'none', background: 'none', cursor: 'pointer', color: mutedColor }}><X size={15} /></button>
                    )}
                </div>
            ))}
            {upravuje && zbyvajici.length > 0 && (
                <div className="flex items-center" style={{ gap: 8, marginTop: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Dropdown value={nova.dovednost_id} isDark={isDark} accent={SECTION_COLOR} height={34} placeholder="Přidat dovednost…"
                            onChange={(v) => setNova((n) => ({ ...n, dovednost_id: v }))}
                            options={zbyvajici.map((d) => ({ value: String(d.id), label: d.nazev, popis: d.sekce?.nazev }))} />
                    </div>
                    <div style={{ width: 120 }}><Dropdown value={nova.min_uroven} isDark={isDark} accent={SECTION_COLOR} height={34} onChange={(v) => setNova((n) => ({ ...n, min_uroven: v }))} options={urovne} /></div>
                    <button type="button" onClick={pridej} disabled={!nova.dovednost_id || ukladam} aria-label="Přidat požadavek"
                        style={{ width: 34, height: 34, borderRadius: 8, border: 'none', cursor: 'pointer', background: SECTION_COLOR, color: '#fff', opacity: nova.dovednost_id ? 1 : 0.4 }}><Plus size={16} /></button>
                </div>
            )}
        </div>
    );
}

const IKONY = ['🍽️', '🚗', '👨‍🍳', '📋', '🏠', '🛒', '🍕', '🥤', '🧹', '📦', '🔧', '⭐', '🎯', '💼', '🚚', '🌙'];

function UpravaKariery({ kariera, isDark, isMobile, inputStyle, onClose, onUlozeno }) {
    const [f, setF] = useState({ nazev: kariera?.nazev || '', ikona: kariera?.ikona || '🍽️', popis: kariera?.popis || '', barva: kariera?.barva || '#6B7280' });
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);
    const uloz = async () => {
        if (!f.nazev.trim()) { setChyba('Vyplň název.'); return; }
        setUkladam(true); setChyba(null);
        try { onUlozeno(await api(kariera ? `/api/konfigurace/kariery/${kariera.id}` : '/api/konfigurace/kariery', kariera ? 'PUT' : 'POST', { ...f, nazev: f.nazev.trim(), kod: kariera?.kod })); }
        catch (e) { setChyba(e.message); setUkladam(false); }
    };
    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={kariera ? `Upravit · ${kariera.nazev}` : 'Nová kariéra'} ikona={Trophy} barva={SECTION_COLOR}
            paticka={(
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onClose} style={{ height: 40, padding: '0 16px', borderRadius: 8, fontSize: 13.5, cursor: 'pointer', flex: isMobile ? 1 : undefined, background: 'transparent', color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>Zrušit</button>
                    <button type="button" onClick={uloz} disabled={ukladam} style={{ height: 40, padding: '0 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', flex: isMobile ? 1 : undefined, background: SECTION_COLOR, color: '#fff', border: 'none' }}>{ukladam ? 'Ukládám…' : kariera ? 'Uložit' : 'Vytvořit'}</button>
                </div>
            )}>
            <div style={{ padding: 16 }}>
                <label style={{ display: 'block', marginBottom: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Název</span>
                    <input value={f.nazev} onChange={(e) => setF((p) => ({ ...p, nazev: e.target.value }))} style={{ ...inputStyle, height: 38 }} autoFocus />
                </label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Ikona</span>
                <div className="flex flex-wrap" style={{ gap: 6, marginBottom: 12 }}>
                    {IKONY.map((i) => (
                        <button key={i} type="button" onClick={() => setF((p) => ({ ...p, ikona: i }))}
                            style={{ width: 40, height: 40, fontSize: 19, borderRadius: 8, cursor: 'pointer', background: 'transparent', border: `1px solid ${f.ikona === i ? SECTION_COLOR : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}` }}>{i}</button>
                    ))}
                </div>
                <label style={{ display: 'block' }}>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Popis</span>
                    <textarea rows={2} value={f.popis} onChange={(e) => setF((p) => ({ ...p, popis: e.target.value }))} style={{ ...inputStyle, height: 'auto', padding: '8px 10px', resize: 'none' }} />
                </label>
                {!kariera && <p className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)', margin: '8px 0 0' }}>Kariéra dostane pět úrovní Novice → Leader, sazby doplníš v detailu.</p>}
                {chyba && <p className="text-sm" style={{ color: CERVENA, margin: '8px 0 0' }}>{chyba}</p>}
            </div>
        </ModalShell>
    );
}
